/**
 * Tee Agent API Service
 *
 * Handles all API calls for the Tee Agent AI assistant
 * Now calls the AI microservice instead of Django backend
 */
import { API_URLS } from "@/api/urls";
import axiosInstance from "@/inteceptors";
import axios from "axios";
import type {
  ChatRequest,
  ChatResponse,
  ExecuteActionRequest,
  ExecuteActionResponse,
  CapabilitiesResponse,
  SessionListResponse,
  SessionDetailResponse,
} from "@/types/teeAgent";

// AI Microservice URL — uses env var, falls back to AKS production URL
const AI_SERVICE_URL =
  import.meta.env.VITE_AI_SERVICE_URL ||
  (import.meta.env.DEV ? "http://localhost:8005" : "https://agent.teelogix.io");

/**
 * Get auth token from axios instance
 * Note: Admin frontend uses "accessToken" (camelCase), not "access_token"
 */
const getAuthToken = (): string | null => {
  const token = localStorage.getItem("accessToken");
  return token;
};

/**
 * Send a chat message to Tee Agent AI Microservice
 * Includes optional screen context for context-aware responses
 */
export const sendChatMessage = async (
  data: ChatRequest
): Promise<ChatResponse> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Not authenticated: missing access token");
  }
  const response = await axios.post<ChatResponse>(
    `${AI_SERVICE_URL}/api/ai/chat`,
    {
      message: data.message,
      session_id: data.session_id,
      screen_context: data.screen_context,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

/**
 * Get list of chat sessions
 */
export const getChatSessions = async (): Promise<SessionListResponse> => {
  const response = await axiosInstance.get<SessionListResponse>(
    API_URLS.TEE_AGENT.SESSIONS
  );
  return response.data;
};

/**
 * Get details of a specific chat session
 */
export const getChatSessionDetail = async (
  sessionId: string
): Promise<SessionDetailResponse> => {
  const response = await axiosInstance.get<SessionDetailResponse>(
    `${API_URLS.TEE_AGENT.SESSION_DETAIL}/${sessionId}/`
  );
  return response.data;
};

/**
 * Delete a chat session
 */
export const deleteChatSession = async (sessionId: string): Promise<void> => {
  await axiosInstance.delete(`${API_URLS.TEE_AGENT.SESSION_DETAIL}/${sessionId}/`);
};

/**
 * Execute or reject a pending action via AI Microservice
 */
export const executeAction = async (
  data: ExecuteActionRequest
): Promise<ExecuteActionResponse> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Not authenticated: missing access token");
  }
  const response = await axios.post<ExecuteActionResponse>(
    `${AI_SERVICE_URL}/api/ai/actions/approve`,
    {
      action_id: data.action_id,
      approved: data.approved,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

/**
 * Get available capabilities based on user permissions
 */
export const getCapabilities = async (): Promise<CapabilitiesResponse> => {
  const response = await axiosInstance.get<CapabilitiesResponse>(
    API_URLS.TEE_AGENT.CAPABILITIES
  );
  return response.data;
};

